import { __ } from '@wordpress/i18n';
import { Input, Select, Label, Checkbox } from '@bsf/force-ui';
import { z } from 'zod';
import TruncateText from '@components/truncate-text';
import { HELP_TEXTS } from '@forms/constants';

const SHOW_MORE_CHARACTER_LIMIT = 50;

const regionOptions = [
	{ label: 'US East (Virginia) - us-east-1', value: 'us-east-1' },
	{ label: 'US East (Ohio) - us-east-2', value: 'us-east-2' },
	{ label: 'US West (N. California) - us-west-1', value: 'us-west-1' },
	{ label: 'US West (Oregon) - us-west-2', value: 'us-west-2' },
	{ label: 'Africa (Cape Town) - af-south-1', value: 'af-south-1' },
	{ label: 'Asia Pacific (Hong Kong) - ap-east-1', value: 'ap-east-1' },
	{ label: 'Asia Pacific (Hyderabad) - ap-south-2', value: 'ap-south-2' },
	{
		label: 'Asia Pacific (Jakarta) - ap-southeast-3',
		value: 'ap-southeast-3',
	},
	{
		label: 'Asia Pacific (Melbourne) - ap-southeast-4',
		value: 'ap-southeast-4',
	},
	{ label: 'Asia Pacific (Mumbai) - ap-south-1', value: 'ap-south-1' },
	{ label: 'Asia Pacific (Osaka) - ap-northeast-3', value: 'ap-northeast-3' },
	{ label: 'Asia Pacific (Seoul) - ap-northeast-2', value: 'ap-northeast-2' },
	{
		label: 'Asia Pacific (Singapore) - ap-southeast-1',
		value: 'ap-southeast-1',
	},
	{
		label: 'Asia Pacific (Sydney) - ap-southeast-2',
		value: 'ap-southeast-2',
	},
	{ label: 'Asia Pacific (Tokyo) - ap-northeast-1', value: 'ap-northeast-1' },
	{ label: 'Canada (Central) - ca-central-1', value: 'ca-central-1' },
	{ label: 'Canada West (Calgary) - ca-west-1', value: 'ca-west-1' },
	{ label: 'Europe (Frankfurt) - eu-central-1', value: 'eu-central-1' },
	{ label: 'Europe (Ireland) - eu-west-1', value: 'eu-west-1' },
	{ label: 'Europe (London) - eu-west-2', value: 'eu-west-2' },
	{ label: 'Europe (Milan) - eu-south-1', value: 'eu-south-1' },
	{ label: 'Europe (Paris) - eu-west-3', value: 'eu-west-3' },
	{ label: 'Europe (Spain) - eu-south-2', value: 'eu-south-2' },
	{ label: 'Europe (Stockholm) - eu-north-1', value: 'eu-north-1' },
	{ label: 'Europe (Zurich) - eu-central-2', value: 'eu-central-2' },
	{ label: 'Israel (Tel Aviv) - il-central-1', value: 'il-central-1' },
	{ label: 'Middle East (Bahrain) - me-south-1', value: 'me-south-1' },
	{ label: 'Middle East (UAE) - me-central-1', value: 'me-central-1' },
	{ label: 'South America (São Paulo) - sa-east-1', value: 'sa-east-1' },
];

export const awsDefaultValues = {
	username: '',
	password: '',
	region: regionOptions[ 0 ].value,
	from_email: '',
	from_name: '',
	force_from_email: true,
	force_from_name: false,
	return_path: true,
	type: 'AWS',
	id: '',
	connection_title: '',
	priority: 1,
};

// Validation Schema for AWS Form.
export const awsFormSchema = z.object( {
	connection_title: z
		.string()
		.min( 1, __( 'Connection title is required', 'suremails' ) ),
	priority: z
		.number()
		.min( 1, __( 'Priority must be at least 1', 'suremails' ) ),
	username: z.string().min( 1, __( 'Access key is required', 'suremails' ) ),
	password: z.string().min( 1, __( 'Secret key is required', 'suremails' ) ),
	region: z.string().min( 1, __( 'Region is required', 'suremails' ) ),
	from_email: z
		.string()
		.email( __( 'Please enter a valid email address', 'suremails' ) ),
	from_name: z.string().optional(),
	force_from_name: z.boolean(),
	force_from_email: z.boolean(),
	return_path: z.boolean(),
	type: z.string(),
	id: z.string(),
} );

const getRegionLabel = ( regionCode ) => {
	const region = regionOptions.find(
		( option ) => option.value === regionCode
	);
	return region ? region.label : '';
};

const AWSForm = ( { connectionData, onChange, errors, inlineValidator } ) => {
	const handleChange = ( field, value ) => {
		onChange?.( { [ field ]: value } );
	};

	return (
		<div className="flex flex-col gap-4">
			{ /* Connection Title */ }
			<div className="flex flex-col p-2">
				<Input
					type="text"
					name="connection_title"
					size="md"
					value={ connectionData?.connection_title || '' }
					onChange={ ( value ) =>
						handleChange( 'connection_title', value )
					}
					onBlur={ inlineValidator }
					error={ errors?.connection_title }
					placeholder={ __(
						'Enter a title for this connection',
						'suremails'
					) }
					label={ __( 'Connection Title', 'suremails' ) }
					className="w-full"
					required
				/>
			</div>

			{ /* Priority */ }
			<div className="flex flex-col gap-1.5 py-2 pl-2">
				<Input
					name="priority"
					type="number"
					size="md"
					value={ connectionData?.priority }
					onChange={ ( value ) =>
						handleChange( 'priority', parseInt( value ) )
					}
					onBlur={ inlineValidator }
					error={ errors?.priority }
					placeholder={ __( 'Enter a number', 'suremails' ) }
					label={ __( 'Connection Sequence', 'suremails' ) }
					className="w-full"
					required
					min={ 1 }
				/>
				<Label tag="p" size="sm" variant="help">
					<TruncateText
						text={ HELP_TEXTS.CONNECTION_SEQUENCE }
						characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
					/>
				</Label>
			</div>

			{ /* Access Key */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="username"
					type="text"
					size="md"
					value={ connectionData?.username || '' }
					onChange={ ( value ) => handleChange( 'username', value ) }
					onBlur={ inlineValidator }
					error={ errors?.username }
					placeholder={ __(
						'Enter your AWS access key',
						'suremails'
					) }
					label={ __( 'Access Key', 'suremails' ) }
					required
					className="w-full"
				/>
			</div>

			{ /* Secret Key */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="password"
					type="password"
					size="md"
					value={ connectionData?.password || '' }
					onChange={ ( value ) => handleChange( 'password', value ) }
					onBlur={ inlineValidator }
					error={ errors?.password }
					placeholder={ __(
						'Enter your AWS secret key',
						'suremails'
					) }
					label={ __( 'Secret Key', 'suremails' ) }
					required
					className="w-full border-border-subtle"
				/>
			</div>

			{ /* Region */ }
			<div className="flex flex-col py-2 pl-2">
				<Label size="sm" className="w-full mb-2" required>
					{ __( 'Region', 'suremails' ) }
				</Label>
				<Select
					value={ connectionData?.region }
					onChange={ ( value ) => handleChange( 'region', value ) }
					className="w-full h-10"
					combobox
				>
					<Select.Button type="button">
						{ getRegionLabel( connectionData?.region ) }
					</Select.Button>
					<Select.Options className="z-999999">
						{ regionOptions.map( ( option ) => (
							<Select.Option
								key={ option.label }
								value={ option.value }
							>
								{ option.label }
							</Select.Option>
						) ) }
					</Select.Options>
				</Select>
			</div>

			{ /* From Email */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="from_email"
					type="email"
					size="md"
					value={ connectionData?.from_email || '' }
					onChange={ ( value ) =>
						handleChange( 'from_email', value )
					}
					onBlur={ inlineValidator }
					placeholder={ __(
						'Enter the email address to send from',
						'suremails'
					) }
					label={ __( 'From Email', 'suremails' ) }
					required
					className="w-full"
					error={ errors?.from_email }
				/>
			</div>
			<div className="p-2">
				<Checkbox
					checked={ connectionData?.force_from_email ?? true }
					size="sm"
					onChange={ ( value ) =>
						handleChange( 'force_from_email', value )
					}
					onBlur={ inlineValidator }
					label={ {
						heading: __( 'Force From Email', 'suremails' ),
						description: (
							<TruncateText
								text={ HELP_TEXTS.FORCE_FROM_EMAIL }
								characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
							/>
						),
					} }
				/>
			</div>

			{ /* Return Path */ }
			<div className="p-2">
				<Checkbox
					checked={ connectionData?.return_path ?? true }
					onChange={ ( value ) =>
						handleChange( 'return_path', value )
					}
					size="sm"
					label={ {
						heading: __( 'Return Path', 'suremails' ),
						description: (
							<TruncateText
								text={ HELP_TEXTS.RETURN_PATH }
								characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
							/>
						),
					} }
				/>
			</div>

			{ /* From Name */ }
			<div className="flex flex-col py-2 pl-2">
				<Input
					name="from_name"
					type="text"
					size="md"
					value={ connectionData?.from_name || '' }
					onChange={ ( value ) => handleChange( 'from_name', value ) }
					onBlur={ inlineValidator }
					placeholder={ __(
						'Enter the name to send from',
						'suremails'
					) }
					label={ __( 'From Name', 'suremails' ) }
					className="w-full"
					error={ errors?.from_name }
				/>
			</div>
			<div className="p-2">
				<Checkbox
					checked={ connectionData?.force_from_name ?? true }
					size="sm"
					onChange={ ( value ) =>
						handleChange( 'force_from_name', value )
					}
					label={ {
						heading: __( 'Force From Name', 'suremails' ),
						description: (
							<TruncateText
								text={ HELP_TEXTS.FORCE_FROM_NAME }
								characterLimit={ SHOW_MORE_CHARACTER_LIMIT }
							/>
						),
					} }
					disabled={ ! connectionData?.from_name }
				/>
			</div>
		</div>
	);
};

export default AWSForm;
